/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface DeferralDueDateProps {
  id: string;
  dueDate: Date;
  triggerValue: number | null;
  notes: string | null;
}

export class DeferralDueDateRecord extends Record<DeferralDueDateProps>({
  id: null as any,
  dueDate: null as any,
  triggerValue: null as any,
  notes: null as any,
}) {
  public static fromJS(json: {
    id: string;
    dueDate: string | number | Date;
    triggerValue: number | null;
    notes: string | null;
  }): DeferralDueDateRecord {
    if (json == null) {
      throw new Error('DeferralDueDateRecord.fromJS: parameter cannot be null');
    }
    return new DeferralDueDateRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'DeferralDueDateJson.id',
      ),
      dueDate: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'dueDate', '0001-01-01T00:00:00.00Z'),
          'DeferralDueDateJson.dueDate',
        ),
      ),
      triggerValue: json.triggerValue,
      notes: json.notes,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    dueDate: string | number | Date;
    triggerValue: number | null;
    notes: string | null;
  }): [DeferralDueDateRecord, Error[]] {
    if (json == null) {
      throw new Error('DeferralDueDateRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new DeferralDueDateRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'DeferralDueDateJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dueDate: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'dueDate', '0001-01-01T00:00:00.00Z'),
              'DeferralDueDateJson.dueDate',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      triggerValue: tryOrDefault(
        () => json.triggerValue,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: DeferralDueDateProps) {
    super(props);
    if (props == null) {
      throw new Error('DeferralDueDateRecord constructor parameter cannot be null');
    }
  }
}

export abstract class DeferralDueDateViewModelBase implements DeferralDueDateProps {
  protected readonly _props: DeferralDueDateProps;
  private _json?: DeferralDueDateProps;

  get id(): string {
    return this._props.id;
  }
  get dueDate(): Date {
    return this._props.dueDate;
  }
  get triggerValue(): number | null {
    return this._props.triggerValue;
  }
  get notes(): string | null {
    return this._props.notes;
  }

  constructor(props: DeferralDueDateProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        dueDate: this._props.dueDate,
        triggerValue: this._props.triggerValue,
        notes: this._props.notes,
      };
    }
    return this._json;
  }
}
